import { createContext, useContext, type ReactNode } from "react";
import { usePortalAuth } from "../PortalAuthContext";
import { useNewConnectionCount } from "../hooks/useNewConnectionCount";

// Stale-after-save round 2 — the unseen-connection badge count used to live
// inside PortalSidebar with its refresh unreachable, so a first view in the
// vendors/customers tab (markViewed) never updated the badge while the
// chrome stayed mounted across ?tab= navigation. Hosting the count at the
// chrome level lets the sidebar render it AND lets the tabs refresh it via
// the useSubmittingMutation refresh contract.

interface NewConnectionBadge {
  count: number;
  refresh: () => Promise<void> | void;
}

const NewConnectionBadgeContext = createContext<NewConnectionBadge | null>(null);

export function NewConnectionBadgeProvider({ children }: { children: ReactNode }) {
  const auth = usePortalAuth();
  const { count, refresh } = useNewConnectionCount(auth.me?.accountType);
  return (
    <NewConnectionBadgeContext.Provider value={{ count, refresh }}>
      {children}
    </NewConnectionBadgeContext.Provider>
  );
}

const NOOP_BADGE: NewConnectionBadge = { count: 0, refresh: () => {} };

/** Safe outside the provider (tests, pre-auth surfaces): count 0, no-op
 *  refresh — the badge just stays off. */
export function useNewConnectionBadge(): NewConnectionBadge {
  return useContext(NewConnectionBadgeContext) ?? NOOP_BADGE;
}
